import type { Logger } from "@expert/logging";

import type { DeadAirClassification } from "./deadAirTypes";

import { isDeadAirCategory } from "./deadAirTaxonomy";

/**
 * Extract the first balanced `{...}` object substring from a string, tracking string literals and
 * escapes so braces inside string values do not throw off the brace count. Also strips leading
 * ```json / ``` code fences. Returns null when no balanced object is present.
 */
function extractJsonObject(raw: string): string | null {
    const text = raw.replace(/```(?:json)?/gi, "");

    const start = text.indexOf("{");
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = start; i < text.length; i++) {
        const char = text[i];

        if (inString) {
            if (escaped) {
                escaped = false;
            } else if (char === "\\") {
                escaped = true;
            } else if (char === '"') {
                inString = false;
            }
            continue;
        }

        if (char === '"') {
            inString = true;
        } else if (char === "{") {
            depth++;
        } else if (char === "}") {
            depth--;
            if (depth === 0) {
                return text.slice(start, i + 1);
            }
        }
    }

    return null;
}

/** Parse a possibly prose-wrapped / fenced JSON string into an object, or null if unparseable. */
function parseLooseJson(responseStr: string): Record<string, unknown> | null {
    try {
        return JSON.parse(responseStr) as Record<string, unknown>;
    } catch {
        const extracted = extractJsonObject(responseStr);
        if (!extracted) return null;
        try {
            return JSON.parse(extracted) as Record<string, unknown>;
        } catch {
            return null;
        }
    }
}

/**
 * Matches the labeled-line reply the prompt asks for inside `bot_response` (see buildDeadAirPrompt).
 * Lazy gaps between labels tolerate the blank lines GAIA's list post-processing may insert, and the
 * answer runs to the end so a multi-line numbered list survives intact. Each label must open a line,
 * so the markers appearing mid-sentence in a prose reply are not read as a classification.
 */
const LABELED_REPLY =
    /^[ \t]*CATEGORY:\s*(\S+)[\s\S]*?^[ \t]*CONFIDENCE:\s*(\S+)[\s\S]*?^[ \t]*ANSWER:[ \t]*([\s\S]*)$/im;

/** The prompt's sentinel for "no answer", standing in for JSON null in the labeled-line format. */
const NO_ANSWER = "NONE";

/** Parse the labeled-line reply into the same raw shape the JSON reply has, or null if it is not one. */
function parseLabeledReply(responseStr: string): Record<string, unknown> | null {
    const match = LABELED_REPLY.exec(responseStr);
    if (!match) return null;

    const [, category, confidence, answer] = match;
    const trimmedAnswer = answer.trim();
    const numericConfidence = Number(confidence);

    return {
        category: category.toUpperCase(),
        // Keep a non-numeric confidence as its raw string so validation rejects it rather than reading 0.
        confidence: Number.isNaN(numericConfidence) ? confidence : numericConfidence,
        answer: trimmedAnswer.toUpperCase() === NO_ANSWER ? null : trimmedAnswer,
    };
}

/**
 * Validate that a value is a finite number within [0,1], otherwise null.
 *
 * Confidence gates whether model-generated text is shown to an expert, so an out-of-range value is
 * rejected rather than clamped: clamping would silently turn a malformed reply (e.g. a percentage-style
 * `"confidence": 85` instead of `0.85`, a common LLM JSON failure mode) into `1.0`, which always clears
 * the 0.8 surfacing threshold.
 */
function validateConfidence(value: unknown): number | null {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    if (value < 0 || value > 1) return null;
    return value;
}

/** Coerce an answer to a non-empty trimmed string, or null. Non-string, non-null values are invalid. */
function normalizeAnswer(value: unknown): { ok: true; answer: string | null } | { ok: false } {
    if (value === null || value === undefined) return { ok: true, answer: null };
    if (typeof value !== "string") return { ok: false };
    const trimmed = value.trim();
    return { ok: true, answer: trimmed === "" ? null : trimmed };
}

/**
 * Robustly parse the assistant's reply into a DeadAirClassification, or null if unusable.
 *
 * `responseBody` is the GAIA success `body` (unknown). The answer text lives at `body.response`
 * (a string), mirroring parseSpellCheckBody. That string is the template's `bot_response`, which the
 * prompt asks to hold labeled lines. A JSON object (prose-wrapped or fenced) is still accepted as a
 * fallback, extracted robustly before validation.
 *
 * Logged reply text goes under `responseText`, not `response`: in production the log pipeline turns a
 * `response` field into `{ statusCode: null }`, which is how 40 parse failures went unreadable.
 */
export function parseDeadAirResponse(responseBody: unknown, logger: Logger): DeadAirClassification | null {
    const payload = responseBody as Record<string, unknown> | null | undefined;
    const responseStr = payload?.response;

    if (typeof responseStr !== "string") {
        logger.warn({ body: responseBody }, "Dead-air response missing 'response' field");
        return null;
    }

    const parsed = parseLabeledReply(responseStr) ?? parseLooseJson(responseStr);
    if (!parsed || typeof parsed !== "object") {
        logger.warn({ responseText: responseStr }, "Failed to parse dead-air response JSON");
        return null;
    }

    const { category, confidence, answer } = parsed;

    if (!isDeadAirCategory(category)) {
        logger.warn({ parsed }, "Dead-air response has invalid category");
        return null;
    }

    const validatedConfidence = validateConfidence(confidence);
    if (validatedConfidence === null) {
        logger.warn({ parsed }, "Dead-air response has invalid confidence");
        return null;
    }

    const normalizedAnswer = normalizeAnswer(answer);
    if (!normalizedAnswer.ok) {
        logger.warn({ parsed }, "Dead-air response has invalid answer");
        return null;
    }

    return {
        category,
        confidence: validatedConfidence,
        answer: normalizedAnswer.answer,
    };
}
